/**
 * AI 输出 schema —— 见 SPEC.md §4.3
 * 用 Zod discriminatedUnion 确保 op 字段驱动后续字段类型。
 *
 * 数值字段统一走 NumLike：允许 number 或可被 parseFloat 的 string，
 * 容错 AI 偶尔的类型漂移（例如把 0.5 输出成 "0.5"）。
 */
import { z } from "zod";

const Color = z.string().regex(/^#[0-9a-fA-F]{6}$/, "颜色需为 #RRGGBB 形式");

const SafeCmd = z
  .string()
  .min(1)
  .max(500)
  .refine(s => !/<script|javascript:|on\w+=/i.test(s), "命令含有危险片段");

const Identifier = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, "标识符仅允许 ASCII 字母数字下划线");

/** number | "1.23" | "1e-3" → number；其他情况抛错 */
const NumLike = z.preprocess(v => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const trimmed = v.trim();
    if (trimmed === "") return v;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : v;
  }
  return v;
}, z.number());

/** 与 NumLike 同思路，但保持整数语义 */
const IntLike = z.preprocess(v => {
  if (typeof v === "number") return Math.trunc(v);
  if (typeof v === "string") {
    const n = Number(v.trim());
    return Number.isFinite(n) ? Math.trunc(n) : v;
  }
  return v;
}, z.number().int());

/** number | "true"/"false" → boolean，容错 AI 把布尔写成字符串 */
const BoolLike = z.preprocess(v => {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes" || s === "on") return true;
    if (s === "false" || s === "0" || s === "no" || s === "off") return false;
  }
  if (typeof v === "number") return v !== 0;
  return v;
}, z.boolean());

export const PhysicsConstantName = z.enum([
  "g", "c", "e", "eps0", "mu0", "k_e", "h", "k_B"
]);

export const Command = z.discriminatedUnion("op", [
  z.object({ op: z.literal("eval"), cmd: SafeCmd }),

  z.object({
    op: z.literal("slider"),
    name: Identifier,
    min: NumLike,
    max: NumLike,
    step: NumLike.refine(n => n > 0, "step 必须 > 0"),
    value: NumLike,
    unit: z.string().max(8).optional(),
    label: z.string().max(40).optional()
  }),

  z.object({
    op: z.literal("animate"),
    target: Identifier,
    speed: NumLike.optional(),
    on: BoolLike,
    repeat: z.enum(["oscillating", "increasing", "once"]).optional()
  }),

  z.object({ op: z.literal("trace"), target: Identifier, on: BoolLike }),

  z.object({
    op: z.literal("style"),
    target: Identifier,
    color: Color.optional(),
    thickness: IntLike.refine(n => n >= 1 && n <= 13, "thickness 应在 1–13 之间").optional(),
    visible: BoolLike.optional(),
    opacity: NumLike.refine(n => n >= 0 && n <= 1, "opacity 应在 0–1 之间").optional(),
    dashed: BoolLike.optional()
  }),

  z.object({
    op: z.literal("view"),
    xmin: NumLike,
    xmax: NumLike,
    ymin: NumLike,
    ymax: NumLike,
    axesUnit: z.tuple([z.string().max(8), z.string().max(8)]).optional()
  }),

  z.object({ op: z.literal("caption"), target: Identifier, text: z.string().max(120) }),
  z.object({ op: z.literal("delete"), target: Identifier }),
  z.object({ op: z.literal("reset") }),

  // —— 物理基元 ——
  z.object({
    op: z.literal("vector"),
    name: Identifier,
    from: z.string().max(80),
    to: z.string().max(80),
    color: Color.optional(),
    label: z.string().max(40).optional()
  }),

  z.object({
    op: z.literal("forceDiagram"),
    at: z.string().max(80),
    forces: z
      .array(
        z.object({
          name: Identifier,
          vec: z.string().max(80),
          color: Color.optional(),
          label: z.string().max(40).optional()
        })
      )
      .min(1)
      .max(8)
  }),

  z.object({
    op: z.literal("physicsTrace"),
    target: Identifier,
    mode: z.enum(["trail", "stroboscopic"]),
    fade: BoolLike.optional()
  }),

  z.object({
    op: z.literal("unitAxes"),
    xUnit: z.string().max(8),
    yUnit: z.string().max(8),
    xLabel: z.string().max(20).optional(),
    yLabel: z.string().max(20).optional()
  }),

  z.object({
    op: z.literal("constants"),
    names: z.array(PhysicsConstantName).min(1).max(8)
  })
]);

export type Command = z.infer<typeof Command>;

export const AIResponse = z.object({
  explanation: z.string().max(500),
  commands: z.array(Command).max(64),
  /** AI 不确定需求时反问用户；有 ask 时 commands 可为空数组 */
  ask: z.string().max(300).optional()
});

export type AIResponse = z.infer<typeof AIResponse>;

/** 把 zod issues 压成单行用户可读消息 */
export function formatZodError(err: z.ZodError): string {
  return err.issues
    .map(i => `${i.path.join(".") || "<root>"}: ${i.message}`)
    .slice(0, 5)
    .join("; ");
}
